import { useEffect } from 'react';
import { useDispatch, useReduxState } from 'hooks';
import api from 'api';
import { useApiData } from 'lib/api';
import { LinkButton, LoadingIndicator } from 'components/common';
import { ErrorPage } from 'components/error';
import { Page, PageContent, PageHeader } from 'components/page';
import { PathViewDetailedInfoSteps, PathViewDetailedInfoSummary } from '.';

import type { GetPathsRes } from 'shared-types/responses';

export type DetailedPath = GetPathsRes['paths'][0];

interface Props {
  pathId: number;
}

const PathViewDetailedInfo = ({ pathId }: Props) => {
  const { dispatch } = useDispatch();
  const { messages, user } = useReduxState();

  const { data, loading, updateData } = useApiData<GetPathsRes>({
    url: `/admin/paths?id=${pathId}`,
    onlyIf: Boolean(user) && Boolean(pathId)
  });

  const path = data?.paths[0];

  const createPathAccessEntry = async () => {
    try {
      await api.post(`/admin/paths/${path.id}/access-entries`);
    } catch (err) {
      await dispatch.handleErrorDispatch(err);
    }
  };

  useEffect(() => {
    if (path && path.userIsEnrolled) createPathAccessEntry();
  }, [path]);

  if (
    (!loading && !data) ||
    (!loading && !pathId) ||
    (!loading && !data?.paths.length)
  )
    return (
      <ErrorPage
        description={messages.errorPermissionMaybe}
        icon="tasks"
        title={messages.pathNotFound}
      />
    );

  return (
    <Page activePathname="/paths">
      <PageHeader simpleBack={true} title={messages.commonGoBack}>
        {path &&
        (path?.owners?.some((owner) => owner.id === user?.id) ||
          user?.roles?.includes('superadmin')) ? (
          <LinkButton
            category="light"
            href={`/admin/paths/${path.id}`}
            size="small"
            text={messages.commonShowAdminPage}
          />
        ) : null}
      </PageHeader>

      <PageContent>
        {loading ? (
          <LoadingIndicator />
        ) : (
          <div className="path">
            <div className="path-summary">
              <PathViewDetailedInfoSummary
                path={path}
                updateData={updateData}
              />
            </div>

            <div className="path-steps">
              <PathViewDetailedInfoSteps path={path} />
            </div>

            <style jsx>{`
              .path {
                display: flex;
                flex-direction: column;
                gap: 30px;
              }
            `}</style>
          </div>
        )}
      </PageContent>
    </Page>
  );
};

export default PathViewDetailedInfo;
